import { Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BrandMark } from '@/components/common/BrandMark';
import { fadeUp } from '@/animations/motionVariants';

/**
 * Focused shell for login / register / password-reset flows.
 * Split-screen: brand panel + form panel. Pages mount inside <Outlet />.
 */
export function AuthLayout() {
  return (
    <div className="grid min-h-svh lg:grid-cols-[1.1fr_1fr]">
      <aside className="relative hidden overflow-hidden lg:block">
        {/* Deep purple gradient canvas */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0a3e] via-[#2d1655] to-[#12062e]" />

        {/* Animated gold orb — floats gently */}
        <motion.div
          className="absolute top-[20%] left-[10%] h-80 w-80 rounded-full bg-gold/12 blur-[100px]"
          animate={{
            x: [0, 30, -20, 0],
            y: [0, -25, 15, 0],
            scale: [1, 1.1, 0.95, 1],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          aria-hidden
        />

        {/* Purple ambient orb */}
        <motion.div
          className="absolute bottom-[15%] right-[10%] h-72 w-72 rounded-full bg-purple-500/15 blur-[90px]"
          animate={{
            x: [0, -20, 25, 0],
            y: [0, 20, -15, 0],
            scale: [1, 0.95, 1.08, 1],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          aria-hidden
        />

        {/* Secondary gold glow — top right */}
        <motion.div
          className="absolute -top-20 -right-20 h-48 w-48 rounded-full bg-gold/8 blur-[70px]"
          animate={{ opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          aria-hidden
        />

        {/* Subtle grain texture */}
        <div className="absolute inset-0 opacity-[0.04] bg-grain-noise mix-blend-overlay" aria-hidden />

        {/* Content */}
        <div className="relative flex h-full flex-col justify-between p-12">
          <BrandMark tone="light" />

          {/* Logo with gold glow */}
          <div className="relative mx-auto w-full max-w-lg">
            <motion.div
              className="absolute -inset-8 rounded-3xl bg-gold/5 blur-2xl"
              animate={{ opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              aria-hidden
            />
            <motion.img
              src="/logo.jpeg"
              alt="DreamPick logo"
              className="relative w-full object-contain drop-shadow-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            />
          </div>

          <motion.div initial="hidden" animate="visible" variants={fadeUp} className="max-w-md">
            <p className="font-display text-4xl leading-tight">
              A quieter kind of speed.
              <span className="block mt-1 bg-gradient-to-r from-gold via-amber-300 to-gold bg-clip-text text-transparent gradient-drift">
                Engineered in India.
              </span>
            </p>
            <p className="mt-6 text-sm leading-relaxed text-white/50">
              Premium electric mobility for a new generation of riders.
            </p>

            {/* Decorative gold line */}
            <motion.div
              className="mt-8 h-px w-24 bg-gradient-to-r from-gold/60 to-transparent"
              initial={{ width: 0 }}
              animate={{ width: '6rem' }}
              transition={{ duration: 1, delay: 0.5 }}
            />
          </motion.div>
        </div>
      </aside>

      {/* Form panel — subtle purple-tinted background */}
      <section className="relative flex items-center justify-center px-6 py-16">
        {/* Subtle ambient glow behind the form */}
        <div className="pointer-events-none absolute top-1/2 left-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/4 blur-[120px]" aria-hidden />
        <div className="pointer-events-none absolute top-1/4 right-1/4 h-64 w-64 rounded-full bg-purple-500/5 blur-[80px]" aria-hidden />

        <div className="relative w-full max-w-sm">
          <Outlet />
        </div>
      </section>
    </div>
  );
}
